import "./styles/index.css";

import { dom } from "./dom";
import { SIDE_RAIL_SECTION_KEY, state } from "./state";

import { attachResizeHandler, startRenderLoop } from "./viewer/scene";
import { isGridVisible, setGridVisible } from "./viewer/sceneTheme";
import { setStatus } from "./ui/status";
import { closeModal, openModal } from "./ui/modals";
import { applySideRailLayout, initSideRail, setActiveSideSection } from "./ui/sideRail";
import { initHelpPopovers } from "./ui/popover";
import { initSubtabs } from "./ui/subtabs";
import { getLang, initI18n, onLangChange, setLang, type Lang } from "./i18n";
import { getThemePreference, initTheme, setThemePreference, type ThemePreference } from "./ui/theme";
import {
  initPwa,
  isPwaInstallAvailable,
  onPwaInstallAvailability,
  promptPwaInstall,
} from "./pwa";

import {
  activateIfcTab,
  addOrReplaceIfcTab,
  closeActiveIfcTab,
  closeIfcTabById,
  renderIfcTabs,
  persistActiveTabFilterState,
} from "./ifc/tabs";
import { frameObject } from "./ifc/loader";
import { initAnalytics, trackTool } from "./analytics/events";
import { centerPlanView } from "./features/planView";
import { toggleXray } from "./features/xray";
import { primeFiltersOnApply } from "./ifc/deferredInit";
import { rebuildMeshVisibility, relabelFilterTypeOptions } from "./ifc/filters";
import {
  applyCompareBaselineFromFile,
  resetCompareState,
  runIfcComparison,
  setShowRemoved,
  updateCompareButtonsState,
} from "./ifc/compare";

const embeddedInCde = import.meta.env.VITE_ANALYTICS_MODE !== "public";

import {
  addMeasurement as _unusedAddMeasurement,
  clearAllMeasurements,
  clearPendingMeasurePoint,
  formatMeasureDistance,
  handleMeasureClick,
  refreshMeasurementLabel,
  removeMeasurementById,
  renderMeasureHistory,
  updateMeasureUi,
} from "./features/measure";
import {
  hideSelectedElement,
  isolateSelectedElements,
  pickIfcElement,
  showAllHiddenElements,
  toggleMultiSelectMode,
  updateMultiSelectUi,
} from "./features/selection";
import { focusCameraOnPointer } from "./features/viewerCamera";
import {
  collapseAllTree,
  expandAllTree,
  handleTreeClick,
  handleTreeDoubleClick,
} from "./features/tree";
import { initClippingControls } from "./features/clipping";
import { initPlanView, syncPlanWithFilters } from "./features/planView";
import {
  handleViewsListClick,
  saveCurrentView,
} from "./features/views";
import { handleSearchResultsClick, runSearch, updateSearchUiState } from "./features/search";
import { initColorRuleControls, syncColorRuleUi } from "./features/colorRules";
import { takeCanvasScreenshot } from "./features/screenshot";
import {
  clearSelectedAnnotationImages,
  closeAnnotationImageMenu,
  deleteAnnotationById,
  exportAnnotationsToWord,
  insertImageFileIntoDescription,
  openAddAnnotationForm,
  openAnnotationImageMenu,
  openEditAnnotationForm,
  renderAnnotationList,
  saveAnnotationFromForm,
  setActiveImageScale,
  updateAnnotationUi,
} from "./features/annotations";

import { closeIfcAddMenu, openIfcAddMenu } from "./ifc/addMenu";
import { initPresentationMenu } from "./ifc/presentationFiles";
import { initPublicWelcome } from "./ui/publicWelcome";
import { exportAnnotationsToBcf, importAnnotationsFromBcf } from "./features/bcf";
import { copyShareLink, copyShareLinkFromInput, tryApplyShareLinkFromLocationHash } from "./features/shareLink";

// «Тень-импорт», чтобы избежать предупреждений о неиспользуемых функциях.
void _unusedAddMeasurement;
void frameObject;
void rebuildMeshVisibility;

// ---- Глобальные обработчики ввода с canvas ---------------------------------

dom.canvas.addEventListener("pointerdown", (event: PointerEvent) => {
  if (event.button !== 0) {
    return;
  }
  state.pointerDownPos = { x: event.clientX, y: event.clientY };
});

dom.canvas.addEventListener("pointerup", async (event: PointerEvent) => {
  if (event.button !== 0 || !state.pointerDownPos) {
    return;
  }
  const dx = event.clientX - state.pointerDownPos.x;
  const dy = event.clientY - state.pointerDownPos.y;
  state.pointerDownPos = null;
  if (dx * dx + dy * dy > 25) {
    return;
  }
  if (state.measureMode) {
    await handleMeasureClick(event);
    return;
  }
  await pickIfcElement(event);
});

// Двойной клик по модели — перенос пивота на точку под курсором и наезд
// ближе к ней (удобно подойти вплотную к объекту в плотной сцене).
dom.canvas.addEventListener("dblclick", (event: MouseEvent) => {
  if (state.measureMode) {
    return;
  }
  void focusCameraOnPointer(event);
});

// ---- Файловый ввод и вкладки -----------------------------------------------

dom.fileInput.addEventListener("change", async (event) => {
  const file = (event.target as HTMLInputElement).files?.[0];
  const mode = state.pendingOpenMode;
  state.pendingOpenMode = "smart";
  const bridgeSource = (window as Window & { __ifcViewerModelSource?: string }).__ifcViewerModelSource;
  delete (window as Window & { __ifcViewerModelSource?: string }).__ifcViewerModelSource;
  if (state.pendingModelSource !== "presentation") {
    state.pendingModelSource = bridgeSource === "cde" ? "cde" : "pc";
    state.pendingPresentationId = null;
  }
  await addOrReplaceIfcTab(file, mode === "replace-active" ? "replace-active" : "new-tab");
  dom.fileInput.value = "";
});

dom.ifcAddFromPcButton.addEventListener("click", async () => {
  closeIfcAddMenu();
  state.pendingOpenMode = "new-tab";
  try {
    if (typeof (dom.fileInput as any).showPicker === "function") {
      await (dom.fileInput as any).showPicker();
      return;
    }
  } catch (_) {
    // fallback below
  }
  dom.fileInput.click();
});

// ---- Мультивыбор (замена Ctrl+клик без клавиатуры) -------------------------

dom.multiselectToggleButton.addEventListener("click", () => {
  toggleMultiSelectMode();
});

// ---- Annotations: BCF export/import ----------------------------------------

dom.annotationExportBcfBtn.addEventListener("click", async () => {
  try {
    await exportAnnotationsToBcf();
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    setStatus(`Экспорт BCF: ${message}`, true);
  }
});

dom.annotationImportBcfBtn.addEventListener("click", () => {
  try {
    if (typeof (dom.annotationImportBcfInput as any).showPicker === "function") {
      (dom.annotationImportBcfInput as any).showPicker();
      return;
    }
  } catch (_) {
    /* ignore */
  }
  dom.annotationImportBcfInput.click();
});

dom.annotationImportBcfInput.addEventListener("change", async (event) => {
  const file = (event.target as HTMLInputElement).files?.[0];
  if (!file) {
    return;
  }
  await importAnnotationsFromBcf(file);
  dom.annotationImportBcfInput.value = "";
});

// ---- Share link ------------------------------------------------------------

dom.shareLinkBtn.addEventListener("click", async () => {
  await copyShareLink();
});

dom.shareLinkCopyBtn.addEventListener("click", async () => {
  await copyShareLinkFromInput();
});

// ---- Общие данные / wireframe / очистка ------------------------------------

dom.fitButton.addEventListener("click", () => {
  if (!state.ifcModel) {
    return;
  }
  if (state.planView.active) {
    centerPlanView();
    return;
  }
  frameObject(state.ifcModel);
});

dom.wireframeButton.addEventListener("click", () => {
  toggleXray();
});

dom.clearButton.addEventListener("click", async () => {
  await closeActiveIfcTab();
});

// ---- Линейка ---------------------------------------------------------------

dom.toggleMeasureButton.addEventListener("click", () => {
  state.measureMode = !state.measureMode;
  if (state.measureMode) trackTool("measure");
  clearPendingMeasurePoint();
  updateMeasureUi();
});

dom.clearMeasureHistoryButton.addEventListener("click", () => {
  clearAllMeasurements();
  setStatus("История замеров очищена.");
});

dom.measureHistoryElement.addEventListener("click", (event) => {
  const target = event.target;
  if (!(target instanceof HTMLButtonElement)) {
    return;
  }
  const rawId = target.dataset.measureId;
  const id = Number(rawId);
  if (!Number.isFinite(id)) {
    return;
  }
  removeMeasurementById(id);
});

dom.measureUnitSelect.addEventListener("change", () => {
  state.measureUnit = dom.measureUnitSelect.value === "mm" ? "mm" : "cm";
  for (const item of state.measureHistory) {
    refreshMeasurementLabel(item);
  }
  renderMeasureHistory();
  if (state.measureHistory.length > 0) {
    const last = state.measureHistory[state.measureHistory.length - 1];
    setStatus(
      `Единицы изменены: ${state.measureUnit}. Последний замер: ${formatMeasureDistance(last.distance)}.`,
    );
  } else {
    setStatus(`Единицы измерения: ${state.measureUnit}.`);
  }
});

dom.measureSnapSelect.addEventListener("change", () => {
  state.measureSnapMode = (dom.measureSnapSelect.value || "surface") as typeof state.measureSnapMode;
  setStatus(`Снэп линейки: ${state.measureSnapMode}`);
});

// ---- Видимость -------------------------------------------------------------

dom.hideSelectedButton.addEventListener("click", () => {
  hideSelectedElement();
});

dom.isolateSelectedButton.addEventListener("click", () => {
  isolateSelectedElements();
});

dom.showAllHiddenButton.addEventListener("click", () => {
  showAllHiddenElements();
});

dom.screenshotButton.addEventListener("click", () => {
  takeCanvasScreenshot();
  trackTool("screenshot");
});

// ---- Дерево IFC ------------------------------------------------------------

dom.treeExpandAllButton.addEventListener("click", () => {
  expandAllTree();
});

dom.treeCollapseAllButton.addEventListener("click", () => {
  collapseAllTree();
});

dom.treeRoot.addEventListener("click", (event) => {
  void handleTreeClick(event);
});

dom.treeRoot.addEventListener("dblclick", (event) => {
  void handleTreeDoubleClick(event);
});

// ---- Виды (закладки) -------------------------------------------------------

dom.viewSaveButton.addEventListener("click", () => {
  saveCurrentView();
});

dom.viewsListElement.addEventListener("click", (event) => {
  handleViewsListClick(event);
});

// ---- Поиск элементов -------------------------------------------------------

dom.searchRunButton.addEventListener("click", () => {
  void runSearch();
});

dom.searchInput.addEventListener("keydown", (event) => {
  if (event.key === "Enter") {
    event.preventDefault();
    void runSearch();
  }
});

dom.searchResultsElement.addEventListener("click", (event) => {
  void handleSearchResultsClick(event);
});

// ---- Фильтры ---------------------------------------------------------------

dom.applyFiltersButton.addEventListener("click", () => {
  state.activeFilters.storeyId = dom.filterStoreySelect.value || "";
  state.activeFilters.types = [...dom.filterTypeSelect.selectedOptions]
    .map((opt) => opt.value)
    .filter(Boolean);
  state.activeFilters.mark = dom.filterMarkInput.value.trim();
  void primeFiltersOnApply().then(() => {
    rebuildMeshVisibility();
    syncPlanWithFilters();
    setStatus("Фильтры применены.");
    trackTool("filter");
    persistActiveTabFilterState();
  });
});

dom.clearFiltersButton.addEventListener("click", () => {
  state.activeFilters.storeyId = "";
  state.activeFilters.types = [];
  state.activeFilters.mark = "";
  dom.filterStoreySelect.value = "";
  [...dom.filterTypeSelect.options].forEach((opt) => {
    opt.selected = false;
  });
  dom.filterMarkInput.value = "";
  rebuildMeshVisibility();
  syncPlanWithFilters();
  setStatus("Фильтры сброшены.");
  persistActiveTabFilterState();
});

// ---- Сравнение -------------------------------------------------------------

dom.compareIfcInput.addEventListener("change", async (event) => {
  const file = (event.target as HTMLInputElement).files?.[0];
  await applyCompareBaselineFromFile(file);
  dom.compareIfcInput.value = "";
});

dom.compareAddFromPcButton.addEventListener("click", async () => {
  try {
    if (typeof (dom.compareIfcInput as any).showPicker === "function") {
      await (dom.compareIfcInput as any).showPicker();
      return;
    }
  } catch (_) {
    /* fall through */
  }
  dom.compareIfcInput.click();
});

if (embeddedInCde) {
  void import("./ifc/compareCde").then((cde) => {
    dom.compareCdeRefreshButton.addEventListener("click", () => {
      void cde.loadCdeVersionList();
    });
    dom.compareCdeListSelect.addEventListener("change", () => {
      updateCompareButtonsState();
    });
    dom.compareCdeOpenButton.addEventListener("click", () => {
      void cde.loadCdeBaselineFromSelectedVersion();
    });
    cde.initCdeCompareUi();
  });
}

dom.runCompareButton.addEventListener("click", () => {
  trackTool("compare");
  runIfcComparison();
});

dom.compareShowRemovedToggle.addEventListener("change", () => {
  setShowRemoved(dom.compareShowRemovedToggle.checked);
});

dom.clearCompareButton.addEventListener("click", () => {
  resetCompareState();
  setStatus("Сравнение IFC сброшено.");
});

// ---- Примечания ------------------------------------------------------------

dom.addAnnotationBtn.addEventListener("click", () => {
  openAddAnnotationForm();
});

dom.openAnnotationsListBtn.addEventListener("click", () => {
  renderAnnotationList();
  openModal(dom.annotationListOverlay);
});

dom.annotationFormSaveBtn.addEventListener("click", () => {
  saveAnnotationFromForm();
});

dom.annotationFormCancelBtn.addEventListener("click", () => {
  closeModal(dom.annotationFormOverlay);
  closeAnnotationImageMenu();
  state.editingAnnotationId = null;
});

dom.annotationFormOverlay.addEventListener("click", (event) => {
  if (event.target === dom.annotationFormOverlay) {
    closeModal(dom.annotationFormOverlay);
    closeAnnotationImageMenu();
    state.editingAnnotationId = null;
  }
});

dom.annotationListOverlay.addEventListener("click", (event) => {
  if (event.target === dom.annotationListOverlay) {
    closeModal(dom.annotationListOverlay);
  }
});

dom.annotationListCloseBtn.addEventListener("click", () => {
  closeModal(dom.annotationListOverlay);
});

dom.annotationExportWordBtn.addEventListener("click", () => {
  exportAnnotationsToWord();
});

dom.annotationListRoot.addEventListener("click", (event) => {
  const target = event.target;
  if (!(target instanceof HTMLElement)) {
    return;
  }
  const delBtn = target.closest<HTMLElement>(".ann-delete-btn");
  const editBtn = target.closest<HTMLElement>(".ann-edit-btn");
  if (delBtn) {
    const raw = delBtn.dataset.annotationId;
    const id = Number(raw);
    if (Number.isFinite(id)) {
      deleteAnnotationById(id);
    }
    return;
  }
  if (editBtn) {
    const raw = editBtn.dataset.annotationId;
    const id = Number(raw);
    if (Number.isFinite(id)) {
      openEditAnnotationForm(id);
    }
  }
});

dom.annotationDescriptionEl.addEventListener("paste", (event) => {
  const clipboardItems = event.clipboardData?.items;
  if (!clipboardItems || clipboardItems.length === 0) {
    return;
  }
  let inserted = false;
  for (const item of clipboardItems) {
    if (item.kind === "file" && item.type.startsWith("image/")) {
      const file = item.getAsFile();
      if (file) {
        event.preventDefault();
        insertImageFileIntoDescription(file);
        inserted = true;
      }
    }
  }
  if (inserted) {
    clearSelectedAnnotationImages();
  }
});

dom.annotationDescriptionEl.addEventListener("drop", (event) => {
  const files = event.dataTransfer?.files;
  if (!files || files.length === 0) {
    return;
  }
  const imageFiles = [...files].filter((f) => f.type.startsWith("image/"));
  if (imageFiles.length === 0) {
    return;
  }
  event.preventDefault();
  dom.annotationDescriptionEl.focus();
  imageFiles.forEach((file) => insertImageFileIntoDescription(file));
});

dom.annotationDescriptionEl.addEventListener("dragover", (event) => {
  const files = event.dataTransfer?.files;
  if (files && files.length > 0) {
    event.preventDefault();
  }
});

dom.annotationDescriptionEl.addEventListener("click", (event) => {
  const target = event.target;
  if (!(target instanceof HTMLElement)) {
    return;
  }
  if (target.tagName === "IMG") {
    clearSelectedAnnotationImages();
    target.classList.add("is-selected");
    state.activeAnnotationImage = target as HTMLImageElement;
    openAnnotationImageMenu(event.clientX + 8, event.clientY + 8);
  } else {
    clearSelectedAnnotationImages();
    closeAnnotationImageMenu();
  }
});

dom.annotationImageMenu.addEventListener("click", (event) => {
  const target = event.target;
  if (!(target instanceof HTMLElement)) {
    return;
  }
  const button = target.closest<HTMLButtonElement>("button");
  if (!button || !state.activeAnnotationImage) {
    return;
  }
  const action = button.dataset.action;
  if (action === "scale") {
    const value = Number(button.dataset.value);
    if (Number.isFinite(value)) {
      setActiveImageScale(value);
    }
  } else if (action === "delete") {
    const toDelete = state.activeAnnotationImage;
    clearSelectedAnnotationImages();
    toDelete.remove();
  }
  closeAnnotationImageMenu();
});

// ---- Tabs ------------------------------------------------------------------

dom.ifcTabsElement.addEventListener("click", async (event) => {
  const target = event.target;
  if (!(target instanceof HTMLElement)) {
    return;
  }
  const closeButton = target.closest<HTMLElement>("[data-close-tab-id]");
  if (closeButton) {
    const closeId = Number(closeButton.dataset.closeTabId);
    if (Number.isFinite(closeId)) {
      await closeIfcTabById(closeId);
    }
    return;
  }
  const plusButton = target.closest<HTMLElement>("#ifc-tab-plus");
  if (plusButton) {
    if (state.ifcAddMenuOpen) {
      closeIfcAddMenu();
    } else {
      openIfcAddMenu(plusButton);
    }
    return;
  }
  const tabButton = target.closest<HTMLElement>("[data-tab-id]");
  if (!tabButton) {
    return;
  }
  const tabId = Number(tabButton.dataset.tabId);
  if (!Number.isFinite(tabId) || tabId === state.activeIfcTabId) {
    return;
  }
  await activateIfcTab(tabId);
});

document.addEventListener("click", (event) => {
  const target = event.target;
  if (!(target instanceof Node)) {
    return;
  }
  const clickedMenu = dom.annotationImageMenu.contains(target);
  const clickedImg = target instanceof HTMLImageElement && dom.annotationDescriptionEl.contains(target);
  if (!clickedMenu && !clickedImg) {
    closeAnnotationImageMenu();
    if (!(target instanceof HTMLElement && dom.annotationDescriptionEl.contains(target))) {
      clearSelectedAnnotationImages();
    }
  }

  const clickedAddMenu = dom.ifcAddMenu.contains(target);
  const clickedPlus =
    target instanceof HTMLElement && Boolean(target.closest("#ifc-tab-plus"));
  const clickedSideBackdrop = dom.sideRailBackdrop.contains(target);
  if (!clickedAddMenu && !clickedPlus && !clickedSideBackdrop) {
    closeIfcAddMenu();
  }
});

// ---- Drag-and-drop IFC в canvas --------------------------------------------

let dropDepth = 0;

function isFileDrag(event: DragEvent): boolean {
  const types = event.dataTransfer?.types;
  if (!types) {
    return false;
  }
  for (let i = 0; i < types.length; i++) {
    if (types[i] === "Files") {
      return true;
    }
  }
  return false;
}

function setDropZoneActive(active: boolean): void {
  dom.dropZoneOverlay.classList.toggle("is-active", active);
  dom.dropZoneOverlay.setAttribute("aria-hidden", String(!active));
}

window.addEventListener("dragenter", (event) => {
  if (!isFileDrag(event)) {
    return;
  }
  event.preventDefault();
  dropDepth += 1;
  setDropZoneActive(true);
});

window.addEventListener("dragover", (event) => {
  if (!isFileDrag(event)) {
    return;
  }
  event.preventDefault();
  if (event.dataTransfer) {
    event.dataTransfer.dropEffect = "copy";
  }
});

window.addEventListener("dragleave", (event) => {
  if (!isFileDrag(event)) {
    return;
  }
  dropDepth = Math.max(0, dropDepth - 1);
  if (dropDepth === 0) {
    setDropZoneActive(false);
  }
});

window.addEventListener("drop", async (event) => {
  if (!isFileDrag(event)) {
    return;
  }
  event.preventDefault();
  dropDepth = 0;
  setDropZoneActive(false);
  const files = event.dataTransfer?.files;
  if (!files || files.length === 0) {
    return;
  }
  const ifcFile = [...files].find((f) => /\.ifc(zip)?$/i.test(f.name));
  if (!ifcFile) {
    setStatus("Можно перетаскивать только .ifc или .ifczip.", true);
    return;
  }
  state.pendingOpenMode = "new-tab";
  await addOrReplaceIfcTab(ifcFile, "new-tab");
});

// ---- Bootstrap -------------------------------------------------------------

// i18n/theme/PWA — поднимаем до initSideRail, чтобы data-i18n атрибуты
// раскрылись до того, как остальные модули прочитают textContent / labels.
initTheme();
initI18n();
initPwa();

dom.settingsLangSelect.value = getLang();
dom.settingsLangSelect.addEventListener("change", () => {
  const value = dom.settingsLangSelect.value as Lang;
  setLang(value);
});
// Подписи IFC-типов в фильтре зависят от языка — переразмечаем при его смене.
onLangChange(() => {
  relabelFilterTypeOptions();
});

dom.settingsThemeSelect.value = getThemePreference();
dom.settingsThemeSelect.addEventListener("change", () => {
  const value = dom.settingsThemeSelect.value as ThemePreference;
  setThemePreference(value);
});

dom.settingsGridToggle.checked = isGridVisible();
dom.settingsGridToggle.addEventListener("change", () => {
  setGridVisible(dom.settingsGridToggle.checked);
});

dom.settingsPwaInstallButton.disabled = !isPwaInstallAvailable();
onPwaInstallAvailability((available) => {
  dom.settingsPwaInstallButton.disabled = !available;
  if (available) {
    dom.settingsPwaHint.hidden = true;
  }
});
dom.settingsPwaInstallButton.addEventListener("click", async () => {
  const outcome = await promptPwaInstall();
  if (outcome === "unavailable") {
    dom.settingsPwaHint.hidden = false;
  }
});
// Если событие `beforeinstallprompt` уже не придёт (Safari/iOS, либо приложение
// уже установлено), показываем подсказку, что установка недоступна.
if (
  !("BeforeInstallPromptEvent" in window) &&
  !window.matchMedia("(display-mode: standalone)").matches
) {
  // Окно ждёт «beforeinstallprompt» 1.5 секунды: если за это время событие
  // не пришло — показываем подсказку. Защищаемся от false-negative в Chrome.
  window.setTimeout(() => {
    if (!isPwaInstallAvailable()) {
      dom.settingsPwaHint.hidden = false;
    }
  }, 1500);
}

initSideRail();
initSubtabs();
initHelpPopovers();
initClippingControls();
initPlanView();
initAnalytics();
initColorRuleControls();
syncColorRuleUi();
updateSearchUiState();
updateMeasureUi();
renderMeasureHistory();
updateAnnotationUi();
updateMultiSelectUi();
renderIfcTabs();
initPresentationMenu();
initPublicWelcome();

updateCompareButtonsState();

// Применяем share-link сразу — функция сама проверит наличие модели и
// корректно отрапортует, если IFC ещё не открыт (в таком случае ссылку
// нужно вспомнить, когда модель загрузится).
tryApplyShareLinkFromLocationHash();

setActiveSideSection(localStorage.getItem(SIDE_RAIL_SECTION_KEY) || "1");
applySideRailLayout();

attachResizeHandler();
startRenderLoop();
